import React, { useCallback, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  APP_ACTIONS,
  APP_VIEW,
} from "../../constants/constant";
import {
  fetchDealers,
  createDealer as createDealerAction,
  updateDealer as updateDealerAction,
  deleteDealer as deleteDealerAction,
} from "../../store/slices/dataManagement";
import ModalMessageFunction from "../../common/modal-message-function";
import ToastMessageFunction from "../../common/toast-message-function";
import { useTranslation } from "../../common/translation-context";
import { checkErrorMsg, getHighRisk, getUserRole } from "../../utils/utils";
import DMGrid from "../../common/data-grid/dmGrid";

const Dealers = () => {
  const { translate: t } = useTranslation();
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.userState.userData);
  const { dealers, loading, actionLoading, error } = useSelector((state) => state.dealers);
  const [actionPerformed, setActionPerformed] = useState(false);
  const [requestType, setRequestType] = useState("");
  const [dealerId, setDealerID] = useState(-1);
  
  const processAction = (action, requestType) => {
    setActionPerformed(action);
    setRequestType(requestType);
  };

  const modalManager = ModalMessageFunction(processAction);
  const toastManager = ToastMessageFunction();
  const showToast = useCallback(
    (message, severity) => {
      toastManager.getToast(message, severity);
    },
    [toastManager]
  );

  const getModalAction = (requestType, requestStatus) => {
    modalManager.getModal(requestType, requestStatus);
  };

  const deleteDealerHandler = async (dealerId) => {
    try {
      await dispatch(deleteDealerAction(dealerId)).unwrap();
      showToast(t("deleteDealerSuccess"), "success");
      dispatch(fetchDealers());
    } catch (error) {
      if (checkErrorMsg(error)) showToast(checkErrorMsg(error), "error");
      else showToast(t("failDeleteDealer"), "error");
    }
  };

  useEffect(() => {
    if (actionPerformed && requestType === "deleteDealer") {
      deleteDealerHandler(dealerId);
    }
    setActionPerformed(false);
    setRequestType("");
  }, [actionPerformed, dealerId]);

  const deleteDealer = (dealerId) => {
    setDealerID(dealerId);
    getModalAction(APP_ACTIONS.DELETEDEALER, "deleteDealer");
  };

  const createDealer = async (values) => {
    values.highRiskDealer =
      getUserRole(userData) === "CCM"
        ? getHighRisk(values.highRiskDealer)
        : values.highRiskDealer;
    try {
      await dispatch(createDealerAction(values)).unwrap();
      showToast(t("addDealerToast"), "success");
      dispatch(fetchDealers());
    } catch (error) {
      if (checkErrorMsg(error)) showToast(checkErrorMsg(error), "error");
      else showToast(t("failAddDealer"), "error");
      dispatch(fetchDealers());
    }
  };

  const editDealer = async (values, id) => {
    values.highRiskDealer =
      getUserRole(userData) === "CCM"
        ? getHighRisk(values.highRiskDealer)
        : values.highRiskDealer;

    if (
      (!values.email || values.email.trim() === "") &&
      (values.highRiskDealer === false || values.highRiskDealer === "false")
    ) {
      await deleteDealerHandler(id);
      return;
    }

    try {
      await dispatch(updateDealerAction({ dealerData: values, id })).unwrap();
      showToast(t("updateDealerToast"), "success");
      dispatch(fetchDealers());
    } catch (error) {
      if (checkErrorMsg(error)) showToast(checkErrorMsg(error), "error");
      else showToast(t("failUpdateDealer"), "error");
    }
  };

  useEffect(() => {
    dispatch(fetchDealers());
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      showToast(error, "error");
    }
  }, [error]);

  return (
    <>
      {modalManager.modal}
      {toastManager.toast}

      <DMGrid
        role={getUserRole(userData)}
        id="table"
        enableEditing={true}
        editDisplayMode="row"
        isLoaderVisible={loading || actionLoading}
        view={APP_VIEW.DM}
        dmView="dealers"
        row={dealers}
        header="dealers"
        count={dealers?.length}
        buttonText="addDealer"
        create={createDealer}
        delete={deleteDealer}
        edit={editDealer}
      />
    </>
  );
};

export default Dealers;
